import { Avatar, Box, Stack, Typography, Modal, IconButton, Tooltip } from "@mui/material";
import { stringAvatar } from "utils/stringAvatar";
import { parseComment } from "./utils/commentParser";
import { WorkspaceUser } from "../../types/workspaceUser";
import { useGetVirtualTourCommentAttachmentsQuery } from "api/virtual-tour/queries/useGetVirtualTourCommentAttachmentsQuery";
import { useGetVirtualTourAnnotationsAttachmentsQuery } from "api/virtual-tour/queries/useGetVirtualTourAnnotationAttachmentsQuery";
import { useState } from "react";
import { useAuth } from "contexts/AuthContext";
import { useAuth0 } from "@auth0/auth0-react";
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { commentStyles } from "./commentStyles";
import { useDeleteAnnotationCommentMutation } from "api/virtual-tour/mutations/useDeleteAnnotationCommentMutation";
import { useEditAnnotationCommentMutation } from "api/virtual-tour/mutations/useEditAnnotationCommentMutation";
import { toast } from "react-toastify";

interface CommentProps {
  comment: VirtualTourAnnotationComment;
  users: WorkspaceUser[];
  fullWidth?: boolean;
  type?: 'comment' | 'annotation';
}

const Comment = ({
  comment,
  users,
  fullWidth = false,
  type = 'comment'
}: CommentProps) => {

  const { user } = useAuth0();

  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const { mutate: deleteAnnotationComment, isPending: isDeletingComment } = useDeleteAnnotationCommentMutation();
  const { mutate: updateAnnotationComment, isPending: isUpdatingComment } = useEditAnnotationCommentMutation();

  if (!comment.message) return null;

  // Find the user's picture from users array
  const commentUser = users.find(u => u.user.id === comment.author.id);
  const picture = commentUser?.user.picture;

  const { data: signedUrls } = useGetVirtualTourCommentAttachmentsQuery(comment.id);

  const handleImageClick = (url: string) => {
    setSelectedImage(url);
  };

  const isAuthorOfComment = comment.author.id === user?.sub;

  const handleDeleteComment = () => {
    deleteAnnotationComment(comment.id, {
      onSuccess: () => {
        toast.success("Comment deleted");
      },
      onError: () => {
        toast.error("Failed to delete comment");
      }
    });
  };

  // const handleEditComment = () => {

  // };

  return (
    <>
      <Box sx={{
        height: 'max-content',
        width: fullWidth ? '100%' : '75%',
        maxWidth: '100%',
      }}>
        <Stack direction="row" spacing={1.5} sx={{
          alignItems: 'center',
          mb: 1,
          px: .5
        }}>
          <Avatar
            src={picture}
            {...(!picture && stringAvatar(comment.author.name))}
            sx={{
              width: 28,
              height: 28,
              fontSize: '0.875rem',
              flexShrink: 0
            }}
          />
          <Typography
            variant="body2"
            sx={{
              color: '#fff',
              fontWeight: 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {comment.author.name}
          </Typography>
        </Stack>
        <Box sx={{
          padding: '.75rem',
          backgroundColor: '#313131',
          borderRadius: '5px',
          color: 'rgba(255, 255, 255, 0.9)',
          fontSize: '0.9375rem',
          overflowWrap: 'break-word',
          wordWrap: 'break-word',
          wordBreak: 'break-word',
          hyphens: 'auto',
        }}>
          {parseComment(comment.message, users, comment, signedUrls, handleImageClick)}
          {isAuthorOfComment && (
            <Stack direction="row" justifyContent={"space-between"} alignItems={"center"}>
              {/* { comment. } */}
              <Stack direction="row" spacing={1}>
                <Typography variant="caption" color="rgba(255, 255, 255, 0.5)">
                  {(new Date(comment.createdAt)).toLocaleDateString()}
                </Typography>
              </Stack>
              <Stack direction="row">
                {/* TODO: edit is currently disabled since i'll have to 
                figure out how to parse and put it into lexical editor's format appropriately*/}
                {/* <Tooltip title="Edit Comment">
                  <IconButton sx={commentStyles.commentActionIconButton} onClick={handleEditComment}>
                    <EditIcon sx={{ fontSize: '1rem' }} />
                  </IconButton>
                </Tooltip> */}
                <Tooltip title="Delete Comment">
                  <IconButton sx={commentStyles.commentActionIconButton} onClick={handleDeleteComment}>
                    <DeleteIcon sx={{ fontSize: '1rem' }} color="error" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
          )}
        </Box>
      </Box>

      {/* Image Modal */}
      <Modal
        open={!!selectedImage}
        onClose={() => setSelectedImage(null)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2
        }}
      >
        <Box
          sx={{
            width: '75vw',
            height: '80vh',
            bgcolor: 'transparent',
            outline: 'none',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'relative',
          }}
          onClick={() => setSelectedImage(null)}
        >
          <img
            src={selectedImage || ''}
            alt="Enlarged view"
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              width: 'auto',
              height: 'auto',
              objectFit: 'contain',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          />
        </Box>
      </Modal>
    </>
  );
};

export default Comment;